import React from 'react';
import styled from '@emotion/styled';
import PropTypes, { InferProps } from 'prop-types';
import { RelativeContainer } from './Card';
import { StyledChip } from './Chip';
import Label from './Label';
import Headline2 from './Typography/Heading/Headline2';
import Body2 from './Typography/Body/Body2';
import SubTitle from './Typography/SubTitle';
import ArticleBody from './InnerHtmlStyling';
import Labels from './Card/Labels';
import grey from './Palettes/grey';
import overlay from './Palettes/overlay';
import colors from './Theme/colors';
import breakpoints from './Theme/breakpoints';
import ReadMoreArrow from './ReadMoreArrow';
import Skeleton from './SkeletonLoader/Skeleton';
import { mediumRoundedCorners } from './RoundedCorners';
import useCompressOnScroll from './lib/useCompressOnScroll';
import useScrollToContent from './lib/useScrollToContent';
import ArticleSample from './ArticleSample';
import ProcessingPaymentComponent from './ProcessingPayment';

export const PartnersArticleContainer = styled.div`
  && {
    a {
      color: ${overlay[900]};
    }
  }
`;

const ArticleHeadline = styled(Headline2)`
  && {
    margin-bottom: 8px;

    @media (min-width: ${breakpoints.md}px) {
      margin-bottom: 20px;
    }
  }
`;

const ArticleDek = styled.div`
  && {
    margin-bottom: 16px;
    font-size: 1.125rem;
    line-height: 1.4;
    color: ${overlay[900]};
    font-weight: 500;

    @media (min-width: ${breakpoints.md}px) {
      font-size: 1.25rem;
      margin-bottom: 24px;
    }
  }
`;

export const ArticleGrid = styled.div`
  display: grid;
  grid-template-areas:
    "breadcrumbs"
    "articleMain"
    "sidebar";
  grid-template-columns: 1fr;
  padding: 0 16px;
  gap: 0 16px;
  box-sizing: border-box;

  &&& {
    max-width: 1100px;
    margin: 0 auto;

    @media (min-width: ${breakpoints.lg}px) {
      grid-template-areas:
        "breadcrumbs breadcrumbs"
        "articleMain sidebar";
      grid-template-columns: 2fr 1fr;
      grid-template-rows: auto 1fr;
      padding: 0px 12px 0 20px;
    }
  }
`;

export const ArticleBreadcrumbsContainer = styled.div`
  grid-area: breadcrumbs;
  margin-bottom: 20px;
  min-width: 0;
`;

export const ArticleMainContainer = styled.article`
  grid-area: articleMain;
  min-width: 0;
`;

export const ArticleSidebar = styled.aside`
  grid-area: sidebar;
  min-width: 0;
`;

const HeaderImage = styled.figure`
  grid-area: image;
`;

const ArticleContainer = styled.div`
  display: grid;
  grid-template-areas:
    "header"
    "content"
    "articleTags"
    "shareButtons"
    "newsletter";
  grid-template-rows: auto 1fr;
  gap: 0;
  background-color: #fff;
  min-height: 100vh;

  @media (min-width: ${breakpoints.md}px) {
    max-width: 1100px;
    margin: 0 auto;
  }

  img {
    width: 100%;
  }
  figure {
    margin: 0;
  }
  && figcaption {
    color: ${grey[650]};
    font-family: ${({ theme }) => theme.fonts.article};
    padding: 0 1px;
    text-align: left;
    line-height: 1.25rem;
    font-size: 12px;
  }
  .image-container {
    margin-bottom: 16px;
    ${mediumRoundedCorners};
    overflow: hidden;
  }
  .image-container + figcaption {
    margin: -16px 0 16px 8px;
  }
`;

const HeaderImageContainer = styled(RelativeContainer)`
  background: rgb(255,255,255);
  background: linear-gradient(180deg,
  rgba(255,255,255,1) 0%, rgba(187,187,187,1) 68%);
  padding-bottom: calc(66.67% + 18px);
  width: calc(100% + 32px);
  left: -16px;

  @media (min-width: ${breakpoints.sm}px) {
    width: 100%;
    left: auto;
    padding-bottom: 66.67%;
  }
`;

const TextContainer = styled.div`
  grid-area: content;
  margin: 0 auto;
  max-width: 550px;
  padding: 16px 0;
  @media print {
    max-width: unset;
    font-size: 200%;
  }
`;

const AuthorContainer = styled.div`
  display: flex;
`;

const AuthorTextWrapper = styled.div`
  padding: 8px 0 0;
`;

const AuthorThumbnailContainer = styled.div`
  border: 1px solid ${grey[325]};
  border-radius: 32px;
  width: 64px;
  height: 64px;
  overflow: hidden;
  margin-right: 16px;
`;

const AuthorName = styled(SubTitle)`
  line-height: ${({ theme }) => theme.typography?.subtitle2?.lineHeight};
  && a {
    color: ${colors.black};
  }
`;

const TranslationLinksWrapper = styled.div`
  text-align: left;
  height: 1.25rem;
  line-height: 1.25rem;
  font-weight: normal;
  font-size: 0.75rem;
  padding: 4px 12px 0 4px;
  font-family: ${({ theme }) => theme.fonts.primary};
  white-space: normal;
  color: ${grey[650]};
  a {
    text-decoration: underline;
  }
  @media (min-width: ${breakpoints.sm}px) {
    padding: 12px 12px 0 0;
    text-align: right;
  }
`;

const Date = styled(SubTitle)`
  line-height: 1rem;
  font-weight: normal;
  color: ${grey[650]};
`;

const TranslatedLabel = styled(SubTitle)`
  line-height: 1.5rem;
  font-weight: normal;
  color: ${grey[650]};
`;

const ArticleFlexSubContainer = styled.div`
  @media (min-width: ${breakpoints.xssm}px) {
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;
    justify-content: space-between;
    align-items: baseline;
  }

  @media (min-width: ${breakpoints.sm}px) {
    flex-direction: row;
    align-items: center;
  }

  @media (min-width: ${breakpoints.md}px) {
    margin-bottom: 0;
  }
`;

const ArticleContentAnchor = styled.span`
  height: 0;
`;

const ArticleHeader = styled.header`
  grid-area: header;
  display: grid;
  grid-template-areas:
    "title"
    "meta"
    "image";

  @media (min-width: ${breakpoints.md}px) {
    grid-template-areas:
      "title title"
      "image image"
      "meta meta";
  }
`;

interface ArticleHeaderTextProperties {
  fullHeightTitle: boolean;
  hasDek?: boolean;
}
const ArticleHeaderText = styled.div<ArticleHeaderTextProperties>`
  grid-area: title;
  display: flex;
  flex-direction: column;
  ${(properties) => properties.fullHeightTitle && `
    min-height: calc(50vh - 100px);
    word-break: break-word;
    justify-content: flex-end;
  `}

  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
    justify-content: flex-start;
  }

  ${(properties) => (properties.hasDek ? `
    h1 {
      margin-bottom: 4px;

      @media (min-width: ${breakpoints.md}px) {
        margin-bottom: 12px;
      }
    }
  ` : '')}
`;

interface ArticleSubContainerProperties {
  fullHeightTitle: boolean;
  scrollY: number;
}
const ARTICLE_SUBHEADER_HEIGHT = 48;

const ArticleSubContainer = styled.div<ArticleSubContainerProperties>`
  grid-area: meta;
  margin: 0 auto;
  min-height: calc(50vh - 0px - 48px);
  width: 100%;

  ${(properties) => properties.fullHeightTitle && `
    min-height: calc(50vh - ${
  properties.scrollY}px - ${
  ARTICLE_SUBHEADER_HEIGHT}px);
    word-break: break-word;
    transition: min-height 0.05s linear;
  `}

  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
  }

  @media (min-width: ${breakpoints.md}px) {
    border-bottom: 1px solid ${grey[200]};
    padding: 16px 0;
    width: 550px;
    min-height: auto;
  }
`;

const ArticleLabel = styled(SubTitle)`
  && {
    margin: 0;
    color: ${overlay[900]};
  }
`;

export const ImageContainer = styled.div`
  width: 102px;
  margin-right: 16px;
`;

export const StyledBody2 = styled(Body2)`
  && {
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
  }
`;

export const TagChip = styled(StyledChip)`
  && {
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-weight: 500;
    font-size: .75rem;
  }
`;

const SocialButtons = styled.div`
  @media(min-width: ${breakpoints.xssm}px) {
    width: 172px;
    overflow: visible;
  }
  div > div {
    padding: 2px;
  }
`;

const TitleSkeleton = styled(Skeleton)`
  min-height: 200px;

  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
  }
`;

const AuthorContainerSkeleton = styled(Skeleton)`
  min-height: 68px;
  min-width: 66%;
  @media (min-width: ${breakpoints.xssm}px) {
    min-width: 200%;
  }
`;

const SocialSkeleton = styled(Skeleton)`
  min-height: 40px;

  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
  }
`;

const DekSkeleton = styled(Skeleton)`
  margin-bottom: 16px;
  min-height: 24px;

  @media (min-width: ${breakpoints.md}px) {
    margin-bottom: 24px;
    min-height: 30px;
  }
`;

interface DynamicShareButtonContainerProperties {
  center: boolean;
}
export const DynamicShareButtonContainer = styled.div<
  DynamicShareButtonContainerProperties
>`
  text-align: ${(properties) => properties.center && 'center'};
  height: 40px;
  margin: 32px 0;
  grid-area: shareButtons;
  @media print {
    display: none;
  }
`;

const getActiveComponent = (
  needsLogin: boolean | null | undefined,
  needsVerification: boolean | null | undefined,
  isProcessingPayment: boolean | null | undefined,
  needsSubscription: boolean | null | undefined,
  needsPersonalAccount: boolean | null | undefined,
) => {
  switch (true) {
    case needsLogin: {
      return 'login';
    }
    case needsVerification: {
      return 'verification';
    }
    case isProcessingPayment: {
      return 'processingPayment';
    }
    case needsSubscription ?? needsPersonalAccount: {
      return 'subscription';
    }
    default: {
      // eslint-disable-next-line unicorn/no-null
      return null;
    }
  }
};

const articleSubHeaderPropertyTypes = {
  insertedAt: PropTypes.node,
  authorName: PropTypes.node,
  authorImage: PropTypes.node,
  autoTranslated: PropTypes.bool,
  autoTranslatedText: PropTypes.string,
  translationLinks: PropTypes.node,
  socialButtons: PropTypes.node,
  sponsorMessage: PropTypes.string,
  pressReleaseMessage: PropTypes.string,
  fullHeightTitle: PropTypes.bool,
  showLoader: PropTypes.bool,
};

type ArticleSubHeaderProperties = InferProps<
  typeof articleSubHeaderPropertyTypes
>;

export const ArticleSubHeader = ({
  authorName,
  authorImage,
  insertedAt,
  socialButtons,
  pressReleaseMessage,
  sponsorMessage,
  fullHeightTitle,
  showLoader,
  autoTranslated,
  autoTranslatedText,
  translationLinks,
}: ArticleSubHeaderProperties) => {
  const { articleContentReference, scrollToContent } = useScrollToContent();
  const scrollY = useCompressOnScroll();

  return (
    <>
      <ArticleSubContainer
        fullHeightTitle={Boolean(fullHeightTitle)}
        scrollY={scrollY}
      >
        {sponsorMessage && showLoader ? (
          <ArticleLabel tag="span">
            <Skeleton variant="rectangular" width="100px" />
          </ArticleLabel>
        ) : <ArticleLabel tag="span">{sponsorMessage}</ArticleLabel>}

        {pressReleaseMessage && showLoader ? (
          <ArticleLabel tag="span">
            <Skeleton variant="rectangular" width="100px" />
          </ArticleLabel>
        ) : <ArticleLabel tag="span">{pressReleaseMessage}</ArticleLabel>}

        <ArticleFlexSubContainer>
          {showLoader ? (
            <AuthorContainer>
              <AuthorContainerSkeleton
                variant="rectangular"
                width="100px"
                height="100%"
              />
            </AuthorContainer>
          ) : (
            <AuthorContainer>
              { authorImage && (
                <AuthorThumbnailContainer>
                  {authorImage}
                </AuthorThumbnailContainer>
              )}
              <AuthorTextWrapper>
                <AuthorName>
                  {authorName}
                </AuthorName>
                <Date tag="p" variant="subtitle2">
                  {insertedAt}
                </Date>
                { autoTranslated && (
                  <TranslatedLabel tag="p" variant="subtitle2">
                    {autoTranslatedText}
                  </TranslatedLabel>
                )}
              </AuthorTextWrapper>
            </AuthorContainer>
          )}
          {showLoader ? (
            <SocialButtons>
              <SocialSkeleton
                variant="rectangular"
                width="100%"
                height="100%"
              />
            </SocialButtons>
          ) : (
            <div>
              <TranslationLinksWrapper>
                {translationLinks}
              </TranslationLinksWrapper>
              <SocialButtons>{socialButtons}</SocialButtons>
            </div>
          )}
        </ArticleFlexSubContainer>
        <ArticleContentAnchor ref={articleContentReference} />
      </ArticleSubContainer>
      {fullHeightTitle && (
        <ReadMoreArrow
          onClick={scrollToContent}
          isHidden={scrollY > 1}
        />
      )}
    </>
  );
};

ArticleSubHeader.propTypes = articleSubHeaderPropertyTypes;

export const ArticleTags = styled.div`
  grid-area: articleTags;
`;

const articlePropertyTypes = {
  title: PropTypes.string,
  dek: PropTypes.string,
  category: PropTypes.shape({
    title: PropTypes.string,
  }),
  children: PropTypes.node,
  translatedCategoryTitle: PropTypes.string,
  authorName: PropTypes.node,
  authorImage: PropTypes.node,
  insertedAt: PropTypes.element,
  autoTranslated: PropTypes.bool,
  autoTranslatedText: PropTypes.string,
  translationLinks: PropTypes.node,
  body: PropTypes.string,
  image: PropTypes.node,
  imageCaption: PropTypes.string,
  socialButtons: PropTypes.node,
  labels: PropTypes.arrayOf(PropTypes.string),
  sponsorMessage: PropTypes.string,
  pressReleaseMessage: PropTypes.string,
  fullHeightTitle: PropTypes.bool,
  showLoader: PropTypes.bool,
  isMemberContent: PropTypes.bool,
  needsLogin: PropTypes.bool,
  needsVerification: PropTypes.bool,
  needsSubscription: PropTypes.bool,
  needsPersonalAccount: PropTypes.bool,
  isProcessingPayment: PropTypes.bool,
  LoginComponent: PropTypes.elementType,
  VerificationComponent: PropTypes.elementType,
  SubscriptionComponent: PropTypes.elementType,
};

type ArticleProperties = InferProps<
  typeof articlePropertyTypes
>;

const Article = ({
  category,
  translatedCategoryTitle,
  title,
  dek,
  authorName,
  authorImage,
  insertedAt,
  autoTranslated,
  autoTranslatedText,
  translationLinks,
  body,
  image,
  imageCaption,
  children,
  socialButtons,
  labels,
  sponsorMessage,
  pressReleaseMessage,
  fullHeightTitle,
  showLoader,
  isMemberContent,
  needsLogin,
  needsVerification,
  needsSubscription,
  needsPersonalAccount,
  isProcessingPayment,
  LoginComponent,
  VerificationComponent,
  SubscriptionComponent,
}: ArticleProperties) => {
  const componentsMap = {
    login: LoginComponent,
    verification: VerificationComponent,
    subscription: SubscriptionComponent,
    processingPayment: ProcessingPaymentComponent,
  };
  const activeComponent = getActiveComponent(
    needsLogin,
    needsVerification,
    isProcessingPayment,
    needsSubscription,
    needsPersonalAccount,
  );
  const accessRestrictionComponent = activeComponent
    ? componentsMap[activeComponent] : undefined;

  const articleBody = accessRestrictionComponent ? (
    <ArticleSample
      body={body}
      Component={accessRestrictionComponent}
    />
  ) : (
    <ArticleBody
      className={isMemberContent ? 'member-content' : undefined}
      dangerouslySetInnerHTML={{ __html: body }}
    />
  );

  const hasFullHeightTitle = Boolean(fullHeightTitle && !activeComponent);

  return (
    <ArticleContainer>
      <ArticleHeader>
        <ArticleHeaderText
          fullHeightTitle={hasFullHeightTitle}
          hasDek={Boolean(dek)}
        >
          {showLoader ? (
            <ArticleHeadline tag="h1">
              <TitleSkeleton variant="rectangular" width="100%">
                &nbsp;
                <br />
                &nbsp;
              </TitleSkeleton>
            </ArticleHeadline>
          ) : <ArticleHeadline tag="h1">{title}</ArticleHeadline>}
          {showLoader ? (
            <DekSkeleton variant="rectangular" width="80%" />
          ) : dek && !accessRestrictionComponent && (
            <ArticleDek>{dek}</ArticleDek>
          )}
        </ArticleHeaderText>
        <HeaderImage>
          {showLoader ? (
            <HeaderImageContainer>
              <Skeleton variant="rectangular" width="100%" height="100%" />
            </HeaderImageContainer>
          ) : (
            !accessRestrictionComponent && image && (
            <>
              <HeaderImageContainer>
                {image}
                <Label>
                  <Labels
                    category={translatedCategoryTitle ?? category?.title}
                    labels={labels}
                  />
                </Label>
              </HeaderImageContainer>
              {imageCaption && (
                <figcaption
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: imageCaption }}
                />
              )}
            </>
            ))}
        </HeaderImage>
        {!accessRestrictionComponent && (
          <ArticleSubHeader
            fullHeightTitle={hasFullHeightTitle}
            authorName={authorName}
            authorImage={authorImage}
            insertedAt={insertedAt}
            autoTranslated={autoTranslated}
            autoTranslatedText={autoTranslatedText}
            translationLinks={translationLinks}
            socialButtons={socialButtons}
            sponsorMessage={sponsorMessage}
            pressReleaseMessage={pressReleaseMessage}
            showLoader={showLoader}
          />
        )}
      </ArticleHeader>
      <TextContainer>
        {showLoader ? (
          <ArticleBody>
            <Skeleton variant="rectangular" width="100%" height="100vh" />
          </ArticleBody>
        ) : articleBody}
      </TextContainer>
      {children}
    </ArticleContainer>
  );
};

Article.propTypes = articlePropertyTypes;

export default Article;
